package com.zi.support.shared

enum class SharedTicketStatus { ALL, UNASSIGNED, ACTIVE, COMPLETED }

object TicketPolicy {
    /** Uses the managed login identity, never an editable display name. Server checks the same identities. */
    fun canAcknowledge(email: String?): Boolean = email.orEmpty().lowercase() in setOf(
        "geraldine@supportapp.internal", "zaheer@supportapp.internal", "damien@supportapp.internal")

    const val PAGE_SIZE: Int = 200
    /** Ticket lists listen to this many newest tickets; filters and searches ask the server for the rest. */
    const val LIST_LIMIT: Int = 300
    /** Stored status values that can mean unassigned / in progress (older releases wrote several spellings). */
    val UNASSIGNED_STATUSES: List<String> = listOf("open", "backlog", "unassigned")
    val ACTIVE_STATUSES: List<String> = listOf("assigned", "in_progress", "inprogress", "started")

    /** A ticket's customer can be changed this many times after creation (firestore.rules enforces it too). */
    const val MAX_CUSTOMER_CHANGES: Int = 2

    /** A chosen resolution path can be switched this many times (firestore.rules enforces it too). */
    const val MAX_RESOLUTION_PATH_CHANGES: Int = 1

    /**
     * Picked on the create form, stored as `ticketCategory` (firestore.rules, createTicketFromCall and the
     * searchTickets API allow exactly these). Replaces the old `ticketType` (user_guide counts as knowledgebase).
     */
    val TICKET_CATEGORIES: List<String> = listOf("support", "knowledgebase")
    const val DEFAULT_CATEGORY: String = "support"

    fun ticketCategoryLabel(category: String): String = if (category == "knowledgebase") "Knowledgebase" else "Support"

    /**
     * Where a ticket came from, for the Tickets tab's source filter. Anything not emailed, portal-made or from the
     * WhatsApp bot (calls too) was made in an app.
     */
    val TICKET_SOURCES: List<String> = listOf("app", "email", "customer_portal", "whatsapp")

    fun ticketSource(sourceType: String): String = sourceType.takeIf { it == "email" || it == "customer_portal" || it == "whatsapp" } ?: "app"

    fun ticketSourceLabel(source: String): String = when (source) {
        "email" -> "Email"
        "customer_portal" -> "Customer portal"
        "whatsapp" -> "WhatsApp"
        else -> "App"
    }

    /**
     * What the problem turned out to be, picked before a ticket can be resolved (stored as `problemCategory`, the
     * label itself). Older tickets have none. firestore.rules only checks it's a short string, so the list can grow.
     */
    val PROBLEM_CATEGORIES: List<String> = listOf(
        "Hardware", "Software / app bug", "User error", "Training needed", "Login / access", "Network / connectivity",
        "Printer", "Scanner / barcode", "Device setup / configuration", "Data / records issue", "Reports", "Email",
        "Integration / API", "Performance / slow", "GPS / tracking", "Power / battery", "Account / billing",
        "Feature request", "App deployment", "Other",
    )

    /**
     * Converts historical status values into the small canonical set used by
     * every client. Older releases wrote both `resolved` and `closed` for a
     * finished ticket, so treating only `completed` as final makes dashboards
     * and filters disagree even though they are reading the same document.
     */
    fun canonicalStatus(status: String): String = when (
        status.trim().lowercase().replace(' ', '_')
    ) {
        "resolved", "closed", "complete", "completed" -> "completed"
        "inprogress", "in_progress", "started" -> "in_progress"
        "assigned" -> "assigned"
        "backlog", "unassigned" -> "backlog"
        else -> "open"
    }

    fun isUnassigned(status: String, assignedUid: String): Boolean =
        canonicalStatus(status) == "backlog" ||
            (canonicalStatus(status) == "open" && assignedUid.isBlank())

    fun isInProgress(status: String, assignedUid: String, isOperationsTicket: Boolean): Boolean =
        canonicalStatus(status) == "in_progress" ||
            (!isOperationsTicket && canonicalStatus(status) == "open" && assignedUid.isNotBlank())

    fun isAssigned(status: String, assignedUid: String): Boolean =
        canonicalStatus(status) == "assigned" && assignedUid.isNotBlank()

    fun isCompleted(status: String): Boolean = canonicalStatus(status) == "completed"

    /** Shown where the update box was: completed tickets take no more updates (also enforced in firestore.rules). */
    const val COMPLETED_NO_UPDATES: String = "This ticket is completed, so no more updates can be added."

    /**
     * Assigned and in progress show as one stage, [ACTIVE_LABEL]. The stored status still tells them apart
     * (picking a resolution path starts work), so the work timer and reports are unchanged.
     */
    fun isActive(status: String, assignedUid: String, isOperationsTicket: Boolean): Boolean =
        isAssigned(status, assignedUid) || isInProgress(status, assignedUid, isOperationsTicket)

    const val ACTIVE_LABEL: String = "Assigned & in progress"

    /** Stored statuses that mean done; "not-in" these finds every open ticket (functions use the same list). */
    val FINISHED_STATUSES: List<String> = listOf("completed", "resolved", "closed", "complete")

    /**
     * Why a ticket belongs on the dashboard's "Needs attention" list, or null: only open tickets with nobody on them.
     * [assignedAtMillis] and [nowMillis] are unused since assigned-but-not-started tickets left the list; kept so the
     * callers (and the iOS argument labels) don't change.
     */
    fun attentionReason(status: String, assignedUid: String, assignedAtMillis: Long?, nowMillis: Long): String? = when {
        isCompleted(status) -> null
        assignedUid.isBlank() || isUnassigned(status, assignedUid) -> "Waiting to be assigned"
        else -> null
    }

    /**
     * The ticket card's one colour (the only colour on the card; see [waitKey]):
     * completed (green); unassigned (red), then unassigned for more than 1 / 3 / 5 days (yellow / orange / pink,
     * counted from creation); assigned / in progress (blue), then assigned and not completed more than 3 days after it
     * was assigned (purple; creation when assignedAt is missing). Never "".
     */
    fun waitTone(status: String, assignedUid: String, createdAtMillis: Long?, assignedAtMillis: Long?, nowMillis: Long): String {
        if (isCompleted(status)) return TONE_DONE
        if (assignedUid.isBlank() || isUnassigned(status, assignedUid)) {
            val days = daysSince(createdAtMillis, nowMillis)
            return when { days > 5 -> WAIT_5_DAYS; days > 3 -> WAIT_3_DAYS; days > 1 -> WAIT_1_DAY; else -> TONE_OPEN }
        }
        return if (daysSince(assignedAtMillis ?: createdAtMillis, nowMillis) > 3) WAIT_ASSIGNED else TONE_ACTIVE
    }

    const val TONE_DONE: String = "completed"
    const val TONE_OPEN: String = "unassigned"
    const val WAIT_1_DAY: String = "unassigned_1"
    const val WAIT_3_DAYS: String = "unassigned_3"
    const val WAIT_5_DAYS: String = "unassigned_5"
    const val TONE_ACTIVE: String = "active"
    const val WAIT_ASSIGNED: String = "assigned_3"

    /** ARGB colour for a [waitTone]. */
    fun waitColor(tone: String): Long = when (tone) {
        TONE_DONE -> 0xFF16A34A
        TONE_OPEN -> 0xFFDC2626
        WAIT_1_DAY -> 0xFFEAB308
        WAIT_3_DAYS -> 0xFFF97316
        WAIT_5_DAYS -> 0xFFEC4899
        TONE_ACTIVE -> 0xFF2563EB
        WAIT_ASSIGNED -> 0xFF7C3AED
        else -> 0
    }

    /** The colour key: each card colour with what it means, in order. */
    val waitKey: List<Pair<String, String>> = listOf(
        TONE_DONE to "Completed",
        TONE_OPEN to "Unassigned",
        WAIT_1_DAY to "Unassigned for more than 1 day",
        WAIT_3_DAYS to "Unassigned for more than 3 days",
        WAIT_5_DAYS to "Unassigned for more than 5 days",
        TONE_ACTIVE to ACTIVE_LABEL,
        WAIT_ASSIGNED to "Assigned more than 3 days ago and not completed",
    )

    /** Short badge for a card that has waited too long, e.g. "Unassigned 4d" or "Assigned 6d ago"; "" otherwise. */
    fun waitBadge(tone: String, createdAtMillis: Long?, assignedAtMillis: Long?, nowMillis: Long): String = when (tone) {
        WAIT_ASSIGNED -> "Assigned ${daysSince(assignedAtMillis ?: createdAtMillis, nowMillis).toInt()}d ago"
        WAIT_1_DAY, WAIT_3_DAYS, WAIT_5_DAYS -> "Unassigned ${daysSince(createdAtMillis, nowMillis).toInt()}d"
        else -> ""
    }

    private fun daysSince(millis: Long?, nowMillis: Long): Double =
        if (millis == null || millis <= 0) 0.0 else (nowMillis - millis) / 86_400_000.0

    /** Sort key for "Needs attention": most urgent priority first. */
    fun priorityRank(priority: String): Int = when (priority.trim().lowercase()) {
        "critical" -> 0
        "high" -> 1
        "low" -> 3
        else -> 2
    }

    fun plannedResolutionForCompletion(resolutionType: String): String = when (
        resolutionType.trim().lowercase().replace(' ', '_')
    ) {
        "office" -> "office"
        "site_visit", "sitevisit" -> "site_visit"
        "training" -> "training"
        else -> resolutionType.trim().lowercase().replace(' ', '_')
    }

    fun matchesStatus(
        status: String,
        assignedUid: String,
        isOperationsTicket: Boolean,
        filter: SharedTicketStatus,
    ): Boolean = when (filter) {
        SharedTicketStatus.ALL -> true
        SharedTicketStatus.UNASSIGNED -> isUnassigned(status, assignedUid)
        SharedTicketStatus.ACTIVE -> isActive(status, assignedUid, isOperationsTicket)
        SharedTicketStatus.COMPLETED -> isCompleted(status)
    }

    fun prefix(collectionName: String): String = when (collectionName) {
        "gantransTickets" -> "GT"
        "kphTickets" -> "KPH"
        "tyetransTickets", "transportdbnTickets", "transportTickets" -> "TPT"
        "operationsTickets" -> "OPS"
        else -> "TKT"
    }

    fun displayNumber(collectionName: String, stored: String, documentId: String): String {
        val prefix = prefix(collectionName)
        val numeric = Regex("(\\d+)$").find(stored.trim())?.groupValues?.get(1)
            ?: Regex("(\\d+)$").find(documentId)?.groupValues?.get(1)
        return if (numeric != null) "$prefix${numeric.padStart(3, '0')}"
        else "$prefix${documentId.take(6).uppercase()}"
    }

    fun companyGroup(collectionName: String, customerName: String, variant: String): String =
        when (collectionName) {
            "gantransTickets" -> "Gantrans"
            "kphTickets" -> "KPH"
            "tyetransTickets", "transportdbnTickets", "transportTickets" -> "Transport"
            "operationsTickets" -> variant.ifBlank { customerName }
            else -> customerName
        }
}
